import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../design-system.css';

const CandidateEditModal = ({ show, onClose, candidateId, candidateData, onSave }) => {
    const [editData, setEditData] = useState({});
    const [metadataFields, setMetadataFields] = useState([]);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loadingPdf, setLoadingPdf] = useState(false);

    useEffect(() => {
        if (show && candidateData) {
            setEditData(candidateData || {});
        }
    }, [show, candidateData]);

    useEffect(() => {
        if (show) {
            fetchMetadataFields();
        }
    }, [show]);

    useEffect(() => {
        if (show && candidateId) {
            setPdfUrl(null);
            setLoadingPdf(true);
            axios.get(`/api/files/download?cvid=${encodeURIComponent(candidateId)}&t=${Date.now()}`, { responseType: 'blob' })
                .then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                    setPdfUrl(url);
                })
                .catch(error => {
                    console.error("Error loading PDF", error);
                    setPdfUrl(null);
                })
                .finally(() => setLoadingPdf(false));
        }
    }, [show, candidateId]);

    const fetchMetadataFields = async () => {
        try {
            const response = await axios.get('/api/metadata/fields');
            setMetadataFields(response.data);
        } catch (error) {
            console.error("Error fetching metadata fields", error);
        }
    };

    const handleMetadataChange = (key, value) => {
        setEditData(prev => ({ ...prev, [key]: value }));
    };

    const handleSaveClick = async () => {
        if (onSave) {
            await onSave(candidateId, editData);
        }
    };

    const handleFileUpload = async (file) => {
        if (!window.confirm("Sei sicuro di voler sostituire il CV attuale con questo file?")) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            setLoadingPdf(true);
            await axios.post(`/api/candidates/${encodeURIComponent(candidateId)}/upload_cv`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Refresh PDF view
            setPdfUrl(null);
            // Add timestamp to force refresh
            const response = await axios.get(`/api/files/download?cvid=${encodeURIComponent(candidateId)}&t=${Date.now()}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            setPdfUrl(url);

            alert("CV sostituito con successo!");
        } catch (error) {
            console.error("Error uploading CV", error);
            const errorMessage = error.response?.data?.detail || "Errore durante il caricamento del nuovo CV.";
            alert(`Errore: ${errorMessage}`);
        } finally {
            setLoadingPdf(false);
        }
    };

    // Helper per campi comuni
    const alwaysMultiline = ['Note Libere I', 'Note Libere II', 'Profilo professionale'];

    const renderField = (label, key, type = "text", options = null) => {
        const value = editData[key] || '';
        const isLongText = (typeof value === 'string' && value.length > 60);
        const shouldBeMultiline = alwaysMultiline.includes(key) || isLongText;

        return (
            <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: 'var(--ds-txt-2)' }}>{label}</label>
                {options ? (
                    <select
                        className="ds-select"
                        value={value}
                        onChange={(e) => handleMetadataChange(key, e.target.value)}
                    >
                        <option value="">-- Seleziona --</option>
                        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                ) : shouldBeMultiline ? (
                    <textarea
                        className="ds-textarea"
                        rows={4}
                        value={value}
                        onChange={(e) => handleMetadataChange(key, e.target.value)}
                    />
                ) : (
                    <input
                        type={type}
                        className="ds-input"
                        value={value}
                        onChange={(e) => handleMetadataChange(key, type === 'number' ? parseFloat(e.target.value) : e.target.value)}
                    />
                )}
            </div>
        );
    };

    if (!show) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content-responsive" style={{ backgroundColor: 'var(--ds-surface)', padding: '28px', borderRadius: 'var(--ds-radius)', width: '800px', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--ds-shadow-lg)' }}>
                <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '18px', fontWeight: 600, color: 'var(--ds-txt)' }}>Modifica Candidato {candidateId}</h2>
                <div className="modal-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                    {metadataFields.map(field => (
                        <div key={field}>
                            {renderField(field, field)}
                        </div>
                    ))}
                </div>

                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                        <input
                            type="file"
                            id="cv-upload-input"
                            style={{ display: 'none' }}
                            accept=".pdf"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) handleFileUpload(file);
                            }}
                        />
                        <button
                            onClick={() => document.getElementById('cv-upload-input').click()}
                            className="ds-btn"
                        >
                            Sostituisci CV
                        </button>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button onClick={onClose} className="ds-btn">Annulla</button>
                        <button onClick={handleSaveClick} className="ds-btn ds-btn-primary">Salva Modifiche</button>
                    </div>
                </div>

                {/* Export Options */}
                <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        onClick={async () => {
                            try {
                                const response = await axios.post(`/api/candidates/${encodeURIComponent(candidateId)}/export_company_cv`, { reasoning: "" }, { responseType: 'blob' });
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', `Profilo_Anonimo_${candidateId}.docx`);
                                document.body.appendChild(link);
                                link.click();
                                link.parentNode.removeChild(link);
                            } catch (e) {
                                console.error("Export error", e);
                                alert("Errore nel download del profilo anonimo.");
                            }
                        }}
                        className="ds-btn"
                        style={{ gap: '6px' }}
                    >
                        📄 Scarica Profilo Anonimo
                    </button>
                </div>

                {/* Inline CV Viewer */}
                <div style={{ marginTop: '24px', borderTop: '0.5px solid var(--ds-border)', paddingTop: '18px' }}>
                    <h3 style={{ marginBottom: '14px', fontSize: '14px', fontWeight: 600, color: 'var(--ds-txt)' }}>Visualizzazione Curriculum</h3>
                    <div style={{ width: '100%', height: '600px', border: '0.5px solid var(--ds-border)', borderRadius: 'var(--ds-radius-sm)', backgroundColor: 'var(--ds-surface-2)' }}>
                        {pdfUrl ? (
                            <iframe
                                src={pdfUrl}
                                title="Curriculum Vitae"
                                width="100%"
                                height="100%"
                                style={{ border: 'none', borderRadius: 'var(--ds-radius-sm)' }}
                            />
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--ds-txt-2)' }}>
                                {loadingPdf ? 'Caricamento anteprima...' : 'Anteprima non disponibile o errore nel caricamento.'}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CandidateEditModal;
